import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Activity, FileText, Layers, Plus, Clock, Lock } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { PLATFORM_FEATURES, BRAND_INFO } from '../data/mock';

const icons = {
  timeline: Activity,
  grid: Layers,
  upload: FileText,
  alacarte: Plus,
  fast: Clock,
  private: Lock,
};

const PlatformPage = () => {
  usePageTitle(
    `Our platform | ${BRAND_INFO.name}`,
    'Anabolic Surge unifies every blood draw into one timeline. 100+ marker grid, free PDF uploads, à la carte panels, and live-streaming results.'
  );

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-16 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">Our platform</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900 max-w-4xl">
            Built for people who treat lab work like a{' '}
            <em className="italic text-brick-500">training log</em>.
          </h1>
          <p className="mt-6 text-lg text-ink-600 max-w-2xl">
            Most lab portals were built for clinicians filing a chart, not for athletes
            tracking their own physiology over the course of years. Anabolic Surge was
            built the other way around.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-cream-100">
        <div className="container-wide">
          <div className="grid md:grid-cols-2 gap-5">
            {PLATFORM_FEATURES.map((f) => {
              const Icon = icons[f.id] || Activity;
              return (
                <article
                  key={f.id}
                  className="bg-cream-50 border border-cream-200 rounded-3xl p-8 hover:border-brick-300 transition-colors"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-cream-100 text-brick-500 border border-cream-200">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-display text-2xl text-ink-900">{f.title}</h3>
                  <p className="mt-3 text-ink-600 text-[15px] leading-relaxed">{f.short}</p>
                  <p className="mt-3 text-ink-700 text-[15px] leading-relaxed">{f.description}</p>
                </article>
              );
            })}
          </div>

          <div className="mt-16 p-10 rounded-3xl bg-charcoal-900 text-cream-50 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="font-display text-display-sm">
                Ready to see what is{' '}
                <em className="italic text-brick-300">actually moving?</em>
              </h3>
              <p className="mt-3 text-cream-200/80 max-w-2xl">
                Order your first panel — or upload a prior LabCorp PDF — and watch your
                full marker timeline appear in your dashboard within hours.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/panels"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brick-500 hover:bg-brick-600 text-white"
              >
                Order labs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default PlatformPage;
